// src/quiz/gre-mock9/verbalData.js
// GRE Mock 9 — Verbal Reasoning.
// Section 1: questions 1–12 (18 min). Section 2: questions 13–27 (23 min).
// Question format: see ../greQuestionTypes.js

const SE_INSTRUCTION = "Select the two answer choices that, when used to complete the sentence, fit the meaning of the sentence as a whole and produce completed sentences that are alike in meaning.";

const MAPS_PASSAGE =
  "Medieval world maps such as the Hereford mappa mundi have often been dismissed as failed attempts at geography: they place Jerusalem at the center, shrink or swell regions according to no consistent scale, and populate distant lands with fabulous creatures. Such judgments, however, assume that these maps were meant to help travelers find their way. Recent scholarship suggests instead that they functioned more like encyclopedias, organizing biblical history, classical legend, and natural lore within a spatial frame that viewers could contemplate. Read this way, the distortions are not errors but choices: a region's size reflected its importance to the story the map told, not its extent on the ground. Medieval sailors, after all, relied on an entirely different kind of chart—the portolan—whose coastlines were often strikingly accurate.";

const BEES_PASSAGE =
  "Honeybee colonies have been declining in many regions, and public attention has focused largely on a single class of pesticides, the neonicotinoids. Laboratory studies show that these chemicals can impair bees' navigation and foraging, and several countries have restricted their use. Yet many entomologists caution against treating the pesticides as the whole story. Parasitic mites, viral infections spread by those mites, and poor nutrition caused by the loss of diverse wildflower habitat have all been linked to colony losses, and these stresses appear to interact: a colony weakened by mites may be far more vulnerable to a dose of pesticide that a healthy colony would survive. If so, policies that address only one factor may yield disappointing results, not because that factor is unimportant, but because the remaining stresses continue to push weakened colonies past the point of collapse.";

const MUSEUM_PASSAGE =
  "For much of the twentieth century, natural history museums arranged their specimens according to a single taxonomic order, presenting the classification of life as settled knowledge. Many museums now display, alongside the specimens, the history of how they were collected and classified, including disputes among scientists and the colonial expeditions that supplied many objects. Critics complain that this approach distracts visitors from nature itself. Defenders reply that the older displays were never neutral: by hiding how knowledge was produced, they encouraged visitors to mistake one period's conclusions for permanent truth.";

const verbalQuestions = [

  // ---------- SECTION 1 ----------

  {
    id: 1,
    question: "Far from being ______, the committee's final report was so brief that several members complained it omitted essential findings.",
    options: ["exhaustive", "cursory", "partisan", "belated", "trivial"],
    answer: "exhaustive",
    explanation: "\"Far from being\" signals the opposite of what follows: a report so brief it left out essential findings was the opposite of exhaustive (thorough). Cursory would agree with the brevity instead of contrasting with it.",
  },
  {
    id: 2,
    type: "blanks",
    question: "The historian's reputation for (i)______ was undeserved: her books, though often accused of bias, in fact presented every faction's claims with (ii)______ care.",
    blanks: [
      { options: ["partiality", "erudition", "brevity"] },
      { options: ["scrupulous", "grudging", "perfunctory"] },
    ],
    answer: ["partiality", "scrupulous"],
    explanation: "Her books were \"accused of bias,\" so the undeserved reputation was for partiality. \"In fact\" introduces the truth: she treated every faction with scrupulous (meticulously fair) care. Grudging and perfunctory care would not refute the charge.",
  },
  {
    id: 3,
    question: "Although the company's founder was famously ______ about her own achievements, she spoke at length and with obvious pride about her employees' work.",
    options: ["reticent", "boastful", "candid", "effusive", "ambivalent"],
    answer: "reticent",
    explanation: "\"Although\" contrasts the blank with speaking at length and with pride, so she said little about herself: reticent. Boastful and effusive describe the opposite.",
  },
  {
    id: 4,
    type: "blanks",
    question: "Early reviewers found the composer's late symphonies (i)______, full of abrupt shifts that seemed to defy any plan; only decades later did analysts show that their apparent disorder (ii)______ a rigorous underlying structure.",
    blanks: [
      { options: ["incoherent", "derivative", "sentimental"] },
      { options: ["concealed", "precluded", "exaggerated"] },
    ],
    answer: ["incoherent", "concealed"],
    explanation: "Music with abrupt shifts that \"defy any plan\" seemed incoherent. Analysts later found a rigorous structure beneath the surface, so the apparent disorder concealed it. Precluded would mean the structure could not exist.",
  },
  {
    id: 5,
    question: "The new regulations were meant to simplify the tax code, but their many exceptions and special cases made it more ______ than ever.",
    options: ["byzantine", "equitable", "transparent", "lenient", "obsolete"],
    answer: "byzantine",
    explanation: "\"But\" signals the opposite of simplification, and many exceptions make a code intricate and hard to follow: byzantine. Transparent would match the original goal, not the result.",
  },
  {
    id: 6,
    question: "Once considered a mere ______ of the larger city, the town has developed a distinctive culture of its own, with festivals and industries that now attract visitors from across the region.",
    options: ["appendage", "rival", "precursor", "paragon", "sanctuary"],
    answer: "appendage",
    explanation: "\"Once considered mere\" plus the town's later independent identity points to something subordinate and attached: an appendage. Rival or paragon would not be described as \"mere.\"",
  },
  {
    id: 7,
    type: "multi",
    selectCount: 2,
    instruction: SE_INSTRUCTION,
    question: "Though the senator's speech was praised for its eloquence, its arguments were ______, relying on emotional appeals rather than evidence.",
    options: ["flimsy", "rigorous", "tenuous", "compelling", "novel", "lengthy"],
    answer: ["flimsy", "tenuous"],
    explanation: "\"Though\" contrasts the eloquence with weak substance, and relying on emotion instead of evidence makes arguments weak: flimsy and tenuous. Rigorous and compelling are the opposite.",
  },
  {
    id: 8,
    type: "multi",
    selectCount: 2,
    instruction: SE_INSTRUCTION,
    question: "The young violinist's technique was so ______ that seasoned critics struggled to find any flaw in her performance.",
    options: ["polished", "erratic", "impeccable", "tentative", "unorthodox", "labored"],
    answer: ["polished", "impeccable"],
    explanation: "Critics found no flaw, so her technique was polished and impeccable (flawless). Erratic, tentative and labored all describe flaws.",
  },
  {
    id: 9,
    type: "multi",
    selectCount: 2,
    instruction: SE_INSTRUCTION,
    question: "Rather than resolving the dispute, the mediator's proposal only ______ it, giving each side fresh grievances.",
    options: ["exacerbated", "settled", "aggravated", "obscured", "postponed", "clarified"],
    answer: ["exacerbated", "aggravated"],
    explanation: "\"Rather than resolving\" and \"fresh grievances\" mean the proposal made the dispute worse: exacerbated and aggravated. Postponed and obscured don't create new grievances.",
  },
  {
    id: 10,
    passage: MAPS_PASSAGE,
    question: "The primary purpose of the passage is to",
    options: [
      "argue that medieval world maps were more geographically accurate than is usually believed",
      "challenge a common judgment about medieval world maps by reconsidering what they were meant to do",
      "compare the Hereford mappa mundi with portolan charts to show which was more useful",
      "explain why medieval mapmakers placed Jerusalem at the center of their maps",
      "criticize recent scholars for overlooking the practical uses of medieval maps",
    ],
    answer: "challenge a common judgment about medieval world maps by reconsidering what they were meant to do",
    explanation: "The passage presents the view that these maps were failed geography, then argues they served a different, encyclopedic purpose, so their distortions were choices. It never claims they were accurate (A).",
  },
  {
    id: 11,
    passage: MAPS_PASSAGE,
    question: "The author mentions portolan charts most likely in order to",
    options: [
      "show that medieval people produced accurate maps when accuracy was their goal",
      "suggest that sailors were unaware of the mappa mundi",
      "argue that portolans eventually replaced world maps",
      "illustrate the fabulous creatures found on medieval maps",
      "concede that the mappa mundi was a failed navigational tool",
    ],
    answer: "show that medieval people produced accurate maps when accuracy was their goal",
    explanation: "The accurate portolan supports the author's point: medieval mapmakers could map precisely when that was the purpose, so the world maps' distortions were deliberate, not a sign of incompetence.",
  },
  {
    id: 12,
    type: "multi",
    passage: MAPS_PASSAGE,
    question: "Which of the following does the passage suggest about the size of regions on medieval world maps?",
    options: [
      "It reflected how important a region was to the narrative the map presented.",
      "It was determined by a consistent mathematical scale.",
      "It was not intended to indicate the region's actual physical extent.",
    ],
    answer: [
      "It reflected how important a region was to the narrative the map presented.",
      "It was not intended to indicate the region's actual physical extent.",
    ],
    explanation: "The passage says size \"reflected its importance to the story the map told, not its extent on the ground\"—supporting the first and third choices. It explicitly says the maps follow \"no consistent scale,\" ruling out the second.",
  },

  // ---------- SECTION 2 ----------

  {
    id: 13,
    question: "The scientist's ______ was legendary: she would repeat an experiment dozens of times before she trusted a result enough to publish it.",
    options: ["circumspection", "impetuosity", "eccentricity", "generosity", "ambition"],
    answer: "circumspection",
    explanation: "The colon explains the blank: endlessly repeating experiments before trusting a result shows caution—circumspection. Impetuosity (rashness) is the opposite.",
  },
  {
    id: 14,
    type: "blanks",
    question: "The memoir's tone is strikingly (i)______; the author recounts even the most painful episodes of her childhood without self-pity, as though (ii)______ events that had happened to someone else.",
    blanks: [
      { options: ["dispassionate", "aggrieved", "whimsical"] },
      { options: ["chronicling", "lamenting", "inventing"] },
    ],
    answer: ["dispassionate", "chronicling"],
    explanation: "Telling painful episodes without self-pity, as if they happened to someone else, is a dispassionate tone; she reports them like a neutral observer—chronicling. Lamenting would contradict \"without self-pity.\"",
  },
  {
    id: 15,
    question: "Critics who expected the director's new film to be as ______ as her earlier work were surprised by its restraint: long silences replace the frantic dialogue of her previous films.",
    options: ["frenetic", "subtle", "somber", "laconic", "measured"],
    answer: "frenetic",
    explanation: "Her earlier films had frantic dialogue, and the new one surprised critics with restraint. So critics expected something frenetic (wildly energetic). Subtle and measured describe the new film, not the old ones.",
  },
  {
    id: 16,
    type: "blanks",
    question: "Although proponents claimed the new drug would (i)______ the disease, the clinical trials showed that it merely (ii)______ symptoms, offering temporary relief without affecting the underlying condition.",
    blanks: [
      { options: ["eradicate", "mimic", "diagnose"] },
      { options: ["alleviated", "intensified", "revealed"] },
    ],
    answer: ["eradicate", "alleviated"],
    explanation: "\"Merely\" and \"without affecting the underlying condition\" contrast with a cure, so proponents claimed it would eradicate the disease. Temporary relief means it alleviated symptoms.",
  },
  {
    id: 17,
    type: "multi",
    selectCount: 2,
    instruction: SE_INSTRUCTION,
    question: "The manager's instructions were so ______ that each team interpreted them differently, and the project quickly fell behind schedule.",
    options: ["nebulous", "explicit", "imprecise", "draconian", "concise", "redundant"],
    answer: ["nebulous", "imprecise"],
    explanation: "Teams reading the instructions differently means they were unclear: nebulous and imprecise. Explicit and concise would prevent the confusion.",
  },
  {
    id: 18,
    type: "multi",
    selectCount: 2,
    instruction: SE_INSTRUCTION,
    question: "Despite his ______ public image, the actor was known among friends for his generosity and quiet kindness.",
    options: ["abrasive", "affable", "prickly", "charming", "reclusive", "modest"],
    answer: ["abrasive", "prickly"],
    explanation: "\"Despite\" sets the public image against generosity and kindness, so the image was harsh: abrasive and prickly. Affable and charming form a pair, but they would not contrast with kindness.",
  },
  {
    id: 19,
    type: "multi",
    selectCount: 2,
    instruction: SE_INSTRUCTION,
    question: "The archive's collection, once ______, has grown so large that researchers now need weeks just to survey its contents.",
    options: ["modest", "vast", "meager", "disorganized", "renowned", "priceless"],
    answer: ["modest", "meager"],
    explanation: "\"Once\" contrasts the past with today's enormous size, so the collection used to be small: modest or meager. Vast describes its present state, not its past.",
  },
  {
    id: 20,
    passage: BEES_PASSAGE,
    question: "The passage is primarily concerned with",
    options: [
      "arguing that neonicotinoid pesticides pose no real threat to honeybees",
      "cautioning against attributing honeybee decline to a single cause",
      "describing laboratory methods used to study bee navigation",
      "proposing a ban on all agricultural pesticides",
      "explaining how parasitic mites spread viral infections",
    ],
    answer: "cautioning against attributing honeybee decline to a single cause",
    explanation: "The passage grants that pesticides harm bees but argues that several interacting stresses drive losses, so single-factor policies may disappoint. It does not deny that pesticides matter (A).",
  },
  {
    id: 21,
    passage: BEES_PASSAGE,
    question: "According to the passage, a colony weakened by mites",
    options: [
      "is immune to the effects of neonicotinoids",
      "may be harmed by a pesticide dose that a healthy colony could survive",
      "recovers quickly once pesticides are restricted",
      "forages more widely to compensate for its losses",
      "is the main cause of the loss of wildflower habitat",
    ],
    answer: "may be harmed by a pesticide dose that a healthy colony could survive",
    explanation: "The passage says a mite-weakened colony \"may be far more vulnerable to a dose of pesticide that a healthy colony would survive.\" That is B.",
  },
  {
    id: 22,
    passage: BEES_PASSAGE,
    question: "Which of the following, if true, would most strengthen the entomologists' caution described in the passage?",
    options: [
      "In a country that banned neonicotinoids, colony losses continued at nearly the same rate where mite infestations were severe.",
      "Neonicotinoids are among the most widely used insecticides in the world.",
      "Some species of wild bees are also affected by neonicotinoids.",
      "Laboratory doses of neonicotinoids are sometimes higher than those found in fields.",
      "Beekeepers have reported colony losses for more than a century.",
    ],
    answer: "In a country that banned neonicotinoids, colony losses continued at nearly the same rate where mite infestations were severe.",
    explanation: "The entomologists warn that addressing only pesticides may disappoint because other stresses persist. A ban followed by continued losses where mites were severe is exactly that outcome. D might weaken the pesticide case, but it says nothing about the other factors.",
  },
  {
    id: 23,
    passage: MUSEUM_PASSAGE,
    question: "The defenders mentioned in the passage would most likely agree with which of the following?",
    options: [
      "Visitors should be shown specimens without any accompanying information.",
      "Displays that conceal how scientific knowledge was produced can mislead visitors.",
      "Taxonomic classification has been entirely discredited.",
      "Colonial expeditions supplied most specimens in every museum.",
      "Museums should stop collecting new specimens.",
    ],
    answer: "Displays that conceal how scientific knowledge was produced can mislead visitors.",
    explanation: "The defenders argue that hiding how knowledge was produced led visitors to \"mistake one period's conclusions for permanent truth\"—that is, it misled them. They never claim taxonomy is discredited (C).",
  },
  {
    id: 24,
    passage: "A city found that neighborhoods with more public libraries have higher average reading scores among schoolchildren. The city council concludes that building additional libraries in neighborhoods with low reading scores will raise those scores.",
    question: "Which of the following, if true, most seriously weakens the council's conclusion?",
    options: [
      "Libraries in the city are open seven days a week.",
      "Libraries were originally built in the neighborhoods where residents had lobbied for them, and those residents already read to their children more often than average.",
      "Some children in neighborhoods with many libraries rarely visit them.",
      "Building a new library takes about two years.",
      "Reading scores in the city have risen slightly over the past decade.",
    ],
    answer: "Libraries were originally built in the neighborhoods where residents had lobbied for them, and those residents already read to their children more often than average.",
    explanation: "The council assumes libraries cause higher scores. B offers another cause: families who already valued reading got the libraries, so new libraries in other neighborhoods may change little. C is a weaker point—an average can be high even if some children never visit.",
  },
  {
    id: 25,
    passage: "Manager: Our competitor cut its prices by 15 percent last year, and its sales rose sharply. If we cut our prices by 15 percent, our profits will rise as well.",
    question: "The manager's reasoning assumes which of the following?",
    options: [
      "The competitor's sales rose for reasons unrelated to its price cut.",
      "The increase in sales after a price cut will be large enough to offset the lower profit on each item sold.",
      "Customers prefer the competitor's products to the manager's company's products.",
      "The competitor will not cut its prices again.",
      "The company's costs will rise next year.",
    ],
    answer: "The increase in sales after a price cut will be large enough to offset the lower profit on each item sold.",
    explanation: "The evidence is about sales; the conclusion is about profits. Higher sales raise profits after a price cut only if the extra volume makes up for the smaller margin on each item. A would undermine the argument, not support it.",
  },
  {
    id: 26,
    passage: "Over the past five years, the number of reported cases of a certain skin condition in Region X has doubled. During the same period, there has been no change in the environmental or genetic factors known to cause the condition.",
    question: "Which of the following, if true, most helps to explain the increase in reported cases?",
    options: [
      "The condition is more common in older adults than in children.",
      "Five years ago, a public awareness campaign began encouraging residents to consult doctors about skin changes, and a new test now identifies mild cases that previously went undetected.",
      "Treatments for the condition have become more expensive.",
      "The population of Region X has remained the same over the five years.",
      "Similar skin conditions are common in neighboring regions.",
    ],
    answer: "Five years ago, a public awareness campaign began encouraging residents to consult doctors about skin changes, and a new test now identifies mild cases that previously went undetected.",
    explanation: "If the causes haven't changed, the true number of cases may not have changed either—only the number reported. More people seeing doctors and a better test both raise reported cases. D rules out population growth but explains nothing.",
  },
  {
    id: 27,
    passage: "A company introduced a four-day work week for its employees and found that productivity per employee rose by 10 percent over the following year. The company's director concludes that the shorter week caused the increase in productivity.",
    question: "Which of the following would be most useful to know in evaluating the director's conclusion?",
    options: [
      "Whether employees prefer Mondays or Fridays off",
      "Whether the company introduced new software or other changes during the same year that could also have raised productivity",
      "How many employees the company had when it was founded",
      "Whether other companies in different industries offer flexible hours",
      "Whether the director proposed the four-day week",
    ],
    answer: "Whether the company introduced new software or other changes during the same year that could also have raised productivity",
    explanation: "To credit the shorter week, other explanations must be ruled out. If new software or other changes arrived at the same time, they might explain the 10 percent rise. The other choices don't bear on the cause.",
  },
];

export default verbalQuestions;
